/**
 * Authentication Hook
 * 
 * React hook for authentication operations
 * Integrates with AuthService and auth store for comprehensive auth management
 * 
 * Features:
 * - Login/logout with proper error handling
 * - Session validation and management
 * - Automatic token refresh
 * - Loading states
 * - Type-safe authentication state
 */

import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/store/auth-store';
import { authApi, LoginCredentials, getTenantId, isValidEmail } from '../api/auth';
import { detectTenantSubdomain } from '../tenant-subdomain';
import { AuthService } from '../services/auth-service';
import toast from 'react-hot-toast';
import { logger } from '@/lib/logger';

// ============================================================================
// CUSTOM AUTHENTICATION HOOK
// ============================================================================

export const useAuth = () => {
  const router = useRouter();
  const {
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    isInitialized,
    sessionExpiresAt,
    login: loginStore,
    logout: logoutStore,
    setLoading,
    setInitialized,
    updateLastActivity,
    isSessionExpired,
    timeUntilExpiry
  } = useAuthStore();

  // ========================================================================
  // SESSION INITIALIZATION
  // ========================================================================

  /**
   * Initialize authentication state on app startup
   * Validates session with backend /auth/me endpoint
   *
   * SECURITY: Always validates session with backend on init
   * This ensures revoked sessions are detected immediately
   */
  const initializeAuth = useCallback(async () => {
    if (isInitialized) return;

    // SKIP validation on public pages to prevent redirect loops
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const isPublicPage = path === '/' || ['/login', '/register', '/forgot-password', '/docs'].some(
        route => path.startsWith(route)
      );

      if (isPublicPage) {
        logger.debug('ℹ️  [useAuth] Skipping auth init on public page');
        setInitialized(true);
        return;
      }
    }

    logger.debug('🚀 [useAuth] Initializing authentication...');
    setLoading(true);

    try {
      // Check if we have stored auth data first
      const storedUser = await AuthService.getStoredUser();
      if (!storedUser) {
        logger.debug('ℹ️  [useAuth] No stored user found');
        setInitialized(true);
        setLoading(false);
        return;
      }

      logger.debug('🔍 [useAuth] Validating session with backend...');

      // SECURITY: Validate session with backend /auth/me
      // This detects revoked sessions immediately
      try {
        const user = await authApi.getCurrentUser();

        if (user && user.email) {
          logger.debug('✅ [useAuth] Session validated:', user.email);
          // Update stored user with fresh data from backend
          useAuthStore.getState().setUser(user);
        } else {
          // Session invalid - clear auth state
          logger.warn('⚠️  [useAuth] Session validation failed - clearing auth');
          logoutStore();
          // Redirect to login with reason
          if (typeof window !== 'undefined') {
            window.location.href = '/login?reason=session_expired';
          }
          return;
        }
      } catch (validationError: any) {
        // Handle 401 specifically - session is invalid
        if (validationError?.response?.status === 401) {
          logger.warn('⚠️  [useAuth] Session expired (401) - clearing auth');
          logoutStore();
          if (typeof window !== 'undefined') {
            window.location.href = '/login?reason=session_expired';
          }
          return;
        }

        // For network errors, fall back to stored user
        // This allows offline access but subsequent API calls will fail
        logger.warn('⚠️  [useAuth] Session validation failed (network), using cached user');
        useAuthStore.getState().setUser(storedUser);
      }

    } catch (error: any) {
      logger.error('❌ [useAuth] Auth initialization failed:', error.message);
      // Clear invalid auth state
      logoutStore();
    } finally {
      setInitialized(true);
      setLoading(false);
    }
  }, [isInitialized, setLoading, setInitialized, logoutStore]);

  // Initialize auth on hook mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // ========================================================================
  // AUTHENTICATION ACTIONS
  // ========================================================================

  /**
   * Login user with email and password
   */
  const loginUser = useCallback(async (credentials: LoginCredentials, redirectTo?: string) => {
    logger.debug('🔐 [useAuth] Login attempt for:', credentials.emailId);
    
    // Validation
    if (!credentials.emailId || !credentials.password) {
      const error = 'Please enter both email and password';
      toast.error(error);
      throw new Error(error);
    }

    if (!isValidEmail(credentials.emailId)) {
      const error = 'Please enter a valid email address';
      toast.error(error);
      throw new Error(error);
    }

    // Add tenant_code only if missing AND we're NOT on a tenant subdomain.
    // On acme.uzo.com / *.dataver.co etc. the backend resolves tenant from
    // the Host header; sending an explicit tenant_code (especially the
    // legacy "default-tenant" fallback) would override that with a value
    // that doesn't exist in the DB → "Organization not found" error.
    if (!credentials.tenant_code) {
      const onTenantSubdomain =
        typeof window !== 'undefined' && !!detectTenantSubdomain(window.location.hostname);
      if (!onTenantSubdomain) {
        credentials.tenant_code = getTenantId();  // Backward-compat for non-subdomain access
      }
    }

    setLoading(true);

    try {
      // Call login API (HttpOnly cookies set by backend automatically)
      const response = await authApi.login(credentials);

      logger.debug('✅ [useAuth] Login successful');

      // Store user and access token in Zustand store
      // Note: access_token is needed for WebSocket authentication (cross-origin)
      // HttpOnly cookies don't work for cross-origin WebSocket connections
      if (response.access_token) {

        // Use at least 1 hour for session expiry, even if backend sends shorter
        const expiresIn = Math.max(response.expires_in || 3600, 3600);

        loginStore(
          response.user,
          response.access_token,
          '', // refresh_token is HttpOnly cookie, not in response body
          expiresIn,
          response.csrf_token // CSRF token for state-changing requests
        );

        // Verify the token was stored
        const storedState = useAuthStore.getState();
        logger.debug('🔑 [useAuth] Token stored in Zustand:', {
          hasAccessToken: !!storedState.accessToken,
          tokenPreview: storedState.accessToken ? storedState.accessToken.substring(0, 20) + '...' : 'NONE',
          hasCsrfToken: !!storedState.csrfToken,
          isAuthenticated: storedState.isAuthenticated
        });
      } else {
        // Fallback if no access_token in response (shouldn't happen)
        logger.warn('⚠️ [useAuth] No access_token in response - WebSocket auth will fail!');
        useAuthStore.getState().setUser(response.user);
      }
      useAuthStore.getState().setInitialized(true);

      logger.debug('✅ [useAuth] Store updated with user info and access token');

      // Show success message
      toast.success(`Welcome back, ${response.user.display_name || response.user.username || response.user.email}!`);

      // Redirect to the intended page (or /chat as default).
      // F-102: validate redirect target — must be a same-origin path, never a
      // full URL or protocol-relative path. Prevents open redirect to phishing.
      const isSafeInternalPath = (p: string) =>
        typeof p === 'string' &&
        p.startsWith('/') &&
        !p.startsWith('//') &&  // protocol-relative URL like //evil.com
        !p.startsWith('/\\');   // backslash-prefix tricks
      // Force-change-password takes precedence over any other destination.
      const destination = (response.user as any)?.must_change_password
        ? '/change-password'
        : (redirectTo && isSafeInternalPath(redirectTo) ? redirectTo : '/chat');
      logger.debug('🎯 [useAuth] Redirecting to:', destination, redirectTo !== destination ? `(rejected unsafe target: ${redirectTo})` : '');

      // Use window.location for more reliable navigation after login
      // router.replace can sometimes fail silently due to React state timing issues
      window.location.href = destination;

    } catch (error: any) {
      const errorMessage = error?.message || 'Login failed. Please try again.';
      logger.error('❌ [useAuth] Login failed:', errorMessage);
      
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, setLoading, loginStore]);

  /**
   * Logout user and clear all auth data
   */
  const logoutUser = useCallback(async () => {
    logger.debug('🚪 [useAuth] Logout initiated');

    // IAM-08: end the SERVER session first, while the token and CSRF token
    // still exist. This used to run last, fire-and-forget, after the store and
    // cookies were already cleared — the request carried no credential, 401'd,
    // and the session and refresh token stayed valid. Capped at 3 s so a slow
    // backend cannot hold the user on the page; errors never block the logout.
    try {
      await Promise.race([
        authApi.logout(),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } catch (error: any) {
      logger.warn('⚠️  [useAuth] Backend logout failed:', error?.message);
    }

    // Then clear the HttpOnly cookies via the Next.js API route
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (e) {
      logger.warn('⚠️  [useAuth] Cookie clear failed:', e);
    }

    // Then local state
    logoutStore();

    // Redirect to login page with logout flag (prevents middleware redirect loop)
    logger.debug('🎯 [useAuth] Redirecting to login...');
    window.location.href = '/login?logout=1';
  }, [logoutStore]);

  /**
   * Refresh authentication token
   * Calls POST /auth/refresh — rotates refresh token, issues new access token
   */
  const refreshToken = useCallback(async () => {
    logger.debug('🔄 [useAuth] Token refresh initiated');

    try {
      const response = await authApi.refreshToken();
      logger.debug('✅ [useAuth] Token refresh successful, expires_in:', response.expires_in);

      // Update access token and session expiry in store
      // Use at least 1 hour for session expiry
      const store = useAuthStore.getState();
      if (response.access_token) {
        const expiresIn = Math.max(response.expires_in || 3600, 3600);
        store.setTokens(response.access_token, '', expiresIn);
      }

      return response;
    } catch (error: any) {
      logger.error('❌ [useAuth] Token refresh failed:', error.message);

      // Only logout if user has been inactive for 15+ minutes
      const store = useAuthStore.getState();
      const inactiveMs = Date.now() - (store.lastActivity || 0);
      const INACTIVE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

      if (inactiveMs > INACTIVE_TIMEOUT_MS) {
        logger.warn('⚠️ [useAuth] Refresh failed + user inactive, logging out');
        toast.error('Session expired. Please login again.');
        await logoutUser();
      } else {
        logger.warn('⚠️ [useAuth] Refresh failed but user is active, will retry');
        // Don't logout — the retry timer or next API call will handle it
      }
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoutUser]);

  /**
   * Validate current session
   */
  const validateSession = useCallback(async (): Promise<boolean> => {
    try {
      const isValid = await authApi.validateSession();
      
      if (!isValid) {
        logger.warn('⚠️  [useAuth] Session validation failed');
        await logoutUser();
        return false;
      }

      // Update last activity
      updateLastActivity();
      return true;
    } catch (error: any) {
      logger.error('❌ [useAuth] Session validation error:', error.message);
      await logoutUser();
      return false;
    }
  }, [logoutUser, updateLastActivity]);

  // ========================================================================
  // TOKEN REFRESH TIMER
  // ========================================================================

  /**
   * Proactively refresh the access token before it expires.
   * Session lifetime is 1 hour; we refresh at 80% (48 minutes).
   * On failure, retry up to 3 times before giving up.
   * This prevents users from hitting 401s during active sessions.
   */
  useEffect(() => {
    if (!isAuthenticated || !isInitialized || !sessionExpiresAt) return;

    const msUntilExpiry = new Date(sessionExpiresAt).getTime() - Date.now();
    if (msUntilExpiry <= 0) return;

    // Refresh at 80% of remaining lifetime, minimum 60s from now
    const refreshIn = Math.max(msUntilExpiry * 0.8, 60_000);

    logger.debug(`⏱️ [useAuth] Scheduling token refresh in ${Math.round(refreshIn / 1000)}s (session expires in ${Math.round(msUntilExpiry / 1000)}s)`);

    const timerId = setTimeout(async () => {
      // Retry refresh up to 3 times with increasing delay
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await refreshToken();
          logger.debug(`✅ [useAuth] Token refresh succeeded (attempt ${attempt})`);
          return; // Success, exit retry loop
        } catch {
          logger.warn(`⚠️ [useAuth] Token refresh attempt ${attempt}/3 failed`);
          if (attempt < 3) {
            // Wait before retrying: 5s, 15s
            await new Promise(r => setTimeout(r, attempt * 5000));
          }
          // After 3 failures, refreshToken() already handles logout
        }
      }
    }, refreshIn);

    return () => clearTimeout(timerId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isInitialized, sessionExpiresAt, refreshToken]);

  // ========================================================================
  // ACTIVITY MONITORING
  // ========================================================================

  /**
   * Track user activity for session management
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleActivity = () => {
      updateLastActivity();
    };

    // Track mouse and keyboard activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, updateLastActivity]);

  // ========================================================================
  // RETURN INTERFACE
  // ========================================================================

  return {
    // State
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    isInitialized,

    // Actions
    login: loginUser,
    logout: logoutUser,
    refreshToken,
    validateSession,

    // Session info
    isSessionExpired: isSessionExpired(),
    timeUntilExpiry: timeUntilExpiry(),
    sessionExpiresAt,

    // Activity tracking
    updateActivity: updateLastActivity
  };
};
